import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["__scopeInformerModal"],
  _excluded2 = ["children"],
  _excluded3 = ["className", "children"],
  _excluded4 = ["__scopeInformerModal"],
  _excluded5 = ["__scopeInformerModal"],
  _excluded6 = ["__scopeInformerModal", "asChild"],
  _excluded7 = ["__scopeInformerModal", "asChild"],
  _excluded8 = ["size"],
  _excluded9 = ["__scopeInformerModal"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import * as React from 'react';
import { clsx } from 'clsx';
import { Button } from '../../../button';
import { DialogDescription as HeadlessDialogDescription, DialogTitle as HeadlessDialogTitle } from '../../../headless/dialog';
import { createDialogScope } from '../../../headless/dialog/context';
import { DomPrimitive } from '../../../internal/components/DomPrimitive';
import { Slot } from '../../../internal/components/Slot';
import { createContextScope } from '../../../internal/context/scoped-context';
import { useComposedRefs } from '../../../internal/hooks/useComposedRefs';
import { composeEventHandlers } from '../../../internal/utils/composeEventHandlers';
import { ActionLink } from '../../../link';
import { ArticleHeading2, ArticleParagraph1 } from '../../../typography';
import { ModalClose, ModalCloseButton, ModalContent, ModalFooter, ModalRoot, ModalTrigger } from '../Modal';
import * as inforerModalStyles from './InformerModal.module.css';
import { jsx as _jsx } from "react/jsx-runtime";
const ROOT_NAME = 'InformerModal';
const BODY_NAME = 'InformerModalBody';
const TITLE_NAME = 'InformerModalTitle';
const CONTENT_NAME = 'InformerModalContent';
const ACTIONS_NAME = 'InformerModalActions';
const ACTION_NAME = 'InformerModalAction';
const ACTION_SECONDARY_NAME = 'InformerModalCancel';
const MEDIA_NAME = 'InformerModalMedia';

/**
 * создаем отдельный скоуп для InformerModal
 * чтобы избежать ошибок композиции контекстов с другими модальными окнами
 */

const [createInformerModalContext, createInformerModalScope] = createContextScope(ROOT_NAME, [createDialogScope]);
const useDialogScope = createDialogScope();
const [InformerModalContentProvider, useInformerModalContentContext] = createInformerModalContext(CONTENT_NAME);

/**
 * HOC для оборачивания компонентов Dialog в скоуп InformerModal
 * при необходимости расширения функциональности, можно создать отдельные компонеты, см InformerModalContent
 */
const withInformerModalScope = Component => {
  const WrappedComponent = props => {
    const {
        __scopeInformerModal
      } = props,
      restProps = _objectWithoutProperties(props, _excluded);
    const dialogScope = useDialogScope(__scopeInformerModal);
    return /*#__PURE__*/_jsx(Component, _objectSpread(_objectSpread({}, dialogScope), restProps));
  };
  WrappedComponent.displayName = "Informer(".concat(Component.displayName || Component.name, ")");
  return WrappedComponent;
};

/**
 * Копоненты Dialog без изменения функционала, обернутые в скоуп InformerModal
 */
export const InformerModalRoot = withInformerModalScope(ModalRoot);
export const InformerModalTrigger = withInformerModalScope(ModalTrigger);
export const InformerModalClose = withInformerModalScope(ModalClose);
export const InformerModalCloseButton = withInformerModalScope(ModalCloseButton);
export const InformerModalFooter = withInformerModalScope(ModalFooter);

/**
 * Компонент для рендера тела модального окна
 */

export const InformerModalBody = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      children
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded2);
  return /*#__PURE__*/_jsx(DomPrimitive.div, _objectSpread(_objectSpread({
    className: inforerModalStyles['body']
  }, rest), {}, {
    ref: ref,
    children: children
  }));
});
InformerModalBody.displayName = BODY_NAME;

/**
 * Компонент для рендера контейнера медиа-изображения в заголовке модального окна
 */

export const InformerModalMedia = /*#__PURE__*/React.forwardRef((_ref2, ref) => {
  let {
      className,
      children
    } = _ref2,
    rest = _objectWithoutProperties(_ref2, _excluded3);
  return /*#__PURE__*/_jsx(DomPrimitive.div, _objectSpread(_objectSpread({
    className: clsx(inforerModalStyles['image'], className)
  }, rest), {}, {
    ref: ref,
    children: children
  }));
});
InformerModalMedia.displayName = MEDIA_NAME;

/**
 * Компонент для рендера заголовка модального окна
 */

export const InformerModalTitle = /*#__PURE__*/React.forwardRef((_ref3, forwardedRef) => {
  let {
      __scopeInformerModal
    } = _ref3,
    rest = _objectWithoutProperties(_ref3, _excluded4);
  const dialogScope = useDialogScope(__scopeInformerModal);
  return /*#__PURE__*/_jsx(DomPrimitive.div, {
    className: inforerModalStyles['title'],
    children: /*#__PURE__*/_jsx(HeadlessDialogTitle, _objectSpread(_objectSpread({}, dialogScope), {}, {
      asChild: true,
      children: /*#__PURE__*/_jsx(ArticleHeading2, _objectSpread(_objectSpread({
        textAlign: "center"
      }, rest), {}, {
        ref: forwardedRef
      }))
    }))
  });
});
InformerModalTitle.displayName = TITLE_NAME;

/**
 * Компонент для рендера описания модального окна
 * описание может использоваться как в заголовке так и в теле модального окна
 */

export const InformerModalDescription = /*#__PURE__*/React.forwardRef((_ref4, forwardedRef) => {
  let {
      __scopeInformerModal
    } = _ref4,
    props = _objectWithoutProperties(_ref4, _excluded5);
  const dialogScope = useDialogScope(__scopeInformerModal);
  return /*#__PURE__*/_jsx(DomPrimitive.div, {
    className: inforerModalStyles['description'],
    children: /*#__PURE__*/_jsx(HeadlessDialogDescription, _objectSpread(_objectSpread({}, dialogScope), {}, {
      asChild: true,
      children: /*#__PURE__*/_jsx(ArticleParagraph1, _objectSpread(_objectSpread({
        textAlign: "center",
        color: "text-secondary-default"
      }, props), {}, {
        ref: forwardedRef
      }))
    }))
  });
});
InformerModalDescription.displayName = 'InformerModalDescription';

/**
 * Компонент обертка InformerModalActions
 * используется для правильного позиционирования кнопок действия
 * @example
 * <InformerModalActions>
 *  <InformerModalCancel>Отмена</InformerModalCancel>
 *  <InformerModalAction>Принять</InformerModalAction>
 * </InformerModalActions>
 */

export const InformerModalActions = /*#__PURE__*/React.forwardRef((_ref5, forwardedRef) => {
  let {
    children
  } = _ref5;
  return /*#__PURE__*/_jsx(DomPrimitive.div, {
    ref: forwardedRef,
    className: inforerModalStyles['actions'],
    children: children
  });
});
InformerModalActions.displayName = ACTIONS_NAME;

/**
 * Button-like компонент для основного действия в модальном окне
 */

export const InformerModalAction = /*#__PURE__*/React.forwardRef((props, forwardedRef) => {
  const {
      __scopeInformerModal,
      asChild
    } = props,
    rest = _objectWithoutProperties(props, _excluded6);
  const {
    actionRef
  } = useInformerModalContentContext(ACTION_SECONDARY_NAME, __scopeInformerModal);
  const dialogScope = useDialogScope(__scopeInformerModal);
  const ref = useComposedRefs(forwardedRef, actionRef);
  const Comp = asChild ? Slot : Button;
  return /*#__PURE__*/_jsx(ModalClose, _objectSpread(_objectSpread({
    asChild: true
  }, dialogScope), {}, {
    children: /*#__PURE__*/_jsx(Comp, _objectSpread(_objectSpread({
      size: "L"
    }, rest), {}, {
      ref: ref
    }))
  }));
});
InformerModalAction.displayName = ACTION_NAME;

/**
 * Button-like компонент для доп действия в модальном окне
 */

export const InformerModalActionSecondary = /*#__PURE__*/React.forwardRef((props, forwardedRef) => {
  const {
      __scopeInformerModal,
      asChild
    } = props,
    rest = _objectWithoutProperties(props, _excluded7);
  const dialogScope = useDialogScope(__scopeInformerModal);
  const Comp = asChild ? Slot : ActionLink;
  return /*#__PURE__*/_jsx("div", {
    className: inforerModalStyles['action-secondary'],
    children: /*#__PURE__*/_jsx(ModalClose, _objectSpread(_objectSpread({
      asChild: true
    }, dialogScope), {}, {
      children: /*#__PURE__*/_jsx(Comp, _objectSpread(_objectSpread({}, rest), {}, {
        ref: forwardedRef
      }))
    }))
  });
});
InformerModalActionSecondary.displayName = ACTION_SECONDARY_NAME;
export const InformerModalContent = /*#__PURE__*/React.forwardRef((_ref6, forwardedRef) => {
  let {
      size = 'S'
    } = _ref6,
    props = _objectWithoutProperties(_ref6, _excluded8);
  const {
      __scopeInformerModal
    } = props,
    contentProps = _objectWithoutProperties(props, _excluded9);
  const dialogScope = useDialogScope(__scopeInformerModal);
  const actionRef = React.useRef(null);
  /**
   * При открытии фокусируемся на кнопке основного действия по умолчанию
   */
  const handleOnOpenAutoFocus = React.useMemo(() => composeEventHandlers(contentProps.onOpenAutoFocus, event => {
    var _actionRef$current;
    event.preventDefault();
    (_actionRef$current = actionRef.current) === null || _actionRef$current === void 0 || _actionRef$current.focus({
      preventScroll: true
    });
  }), [contentProps.onOpenAutoFocus]);
  return /*#__PURE__*/_jsx(InformerModalContentProvider, {
    actionRef: actionRef,
    scope: __scopeInformerModal,
    children: /*#__PURE__*/_jsx(ModalContent, _objectSpread(_objectSpread(_objectSpread({}, dialogScope), contentProps), {}, {
      size: size,
      portalProps: dialogScope,
      overlayProps: dialogScope,
      onOpenAutoFocus: handleOnOpenAutoFocus,
      ref: forwardedRef
    }))
  });
});
InformerModalContent.displayName = CONTENT_NAME;
export { createInformerModalScope };